import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from '../../services/authService';
import { memoryService, MemoryPlace, MemoryConnection } from '../../services/memoryService';
import { getUrlType } from '../../utils/memoryPalaceUtils';
import { NotesService, NoteWithBlocks, Note } from '../../services/notesService';
import { useBlockManager } from '../../hooks/useBlockManager';
import { BlockContainer } from '../../services/blockManager';
import { uploadPlaceImage } from '../../services/imageService';
import PageHeader from '../common/PageHeader';
import PlaceForm from './PlaceForm';
import ConnectionForm from './ConnectionForm';
import PlaceImageGenerator from './PlaceImageGenerator';
import ImageViewer from '../common/ImageViewer';
import BlockEditor from '../notes/BlockEditor';
import MemoryPalaceGraph from './MemoryPalaceGraph';
import ObjectForm, { PlaceObject } from './ObjectForm';
import ObjectsCanvas, { ObjectsCanvasRef } from './ObjectsCanvas';
import './MemoryPalace.css';

interface PlaceDetailProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const PlaceDetail: React.FC<PlaceDetailProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [place, setPlace] = useState<MemoryPlace | null>(null);
  const [outgoingConnections, setOutgoingConnections] = useState<MemoryConnection[]>([]);
  const [incomingConnections, setIncomingConnections] = useState<MemoryConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [showConnectionForm, setShowConnectionForm] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showImageGenerator, setShowImageGenerator] = useState(false);
  const [viewingImage, setViewingImage] = useState<string | null>(null);
  const [showCreatePlaceForm, setShowCreatePlaceForm] = useState(false);
  const [autoConnect, setAutoConnect] = useState(false);
  const [activeTab, setActiveTab] = useState<'description' | 'notes' | 'graph' | 'objects'>('graph');
  const [linkedNote, setLinkedNote] = useState<NoteWithBlocks | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [connectedPlaces, setConnectedPlaces] = useState<MemoryPlace[]>([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState('');
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const linkedNoteRef = useRef<NoteWithBlocks | null>(null);
  const [objects, setObjects] = useState<PlaceObject[]>([]);
  const [showObjectForm, setShowObjectForm] = useState(false);
  const [editingObject, setEditingObject] = useState<PlaceObject | null>(null);
  const [initialObjectPosition, setInitialObjectPosition] = useState<{ x: number; y: number } | undefined>(undefined);
  const saveObjectsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const objectsCanvasRef = useRef<ObjectsCanvasRef>(null);
  const [availableNotes, setAvailableNotes] = useState<Note[]>([]);
  const [loadingAvailableNotes, setLoadingAvailableNotes] = useState(false);
  const [showNoteSelection, setShowNoteSelection] = useState(false);
  const [showLinkExistingNote, setShowLinkExistingNote] = useState(false);
  const [noteSearchQuery, setNoteSearchQuery] = useState('');
  const [isFullBlockMode, setIsFullBlockMode] = useState(false);
  const [allPlaces, setAllPlaces] = useState<MemoryPlace[]>([]);
  const [loadingAllPlaces, setLoadingAllPlaces] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user?.id && id) {
      loadPlaceData();
    }
  }, [user, id]);

  // Load all places for connection form
  useEffect(() => {
    if (user?.id) {
      loadAllPlaces();
    }
  }, [user?.id]);

  // Update document title when place loads
  useEffect(() => {
    if (place) {
      document.title = `/${place.name} - Memory Palace`;
    } else {
      document.title = 'Memory Palace';
    }
    
    // Cleanup: reset title when component unmounts
    return () => {
      document.title = 'Memory Land';
    };
  }, [place]);

  // Update description value when place changes
  useEffect(() => {
    if (place) {
      setDescriptionValue(place.description || '');
    }
  }, [place?.description]);

  // Keep linkedNoteRef in sync with linkedNote state
  useEffect(() => {
    linkedNoteRef.current = linkedNote;
  }, [linkedNote]);

  // Focus textarea when editing starts
  useEffect(() => {
    if (editingDescription && descriptionTextareaRef.current) {
      descriptionTextareaRef.current.focus();
      // Move cursor to end
      const length = descriptionTextareaRef.current.value.length;
      descriptionTextareaRef.current.setSelectionRange(length, length);
    }
  }, [editingDescription]);

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadAllPlaces = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoadingAllPlaces(true);
      const response = await memoryService.getUserPlaces(user.id, 1000);
      if (response.success && response.places) {
        setAllPlaces(response.places);
      }
    } catch (err) {
      console.error('Error loading all places:', err);
    } finally {
      setLoadingAllPlaces(false);
    }
  }, [user?.id]);

  const loadPlaceData = useCallback(async () => {
    if (!user?.id || !id) return;

    try {
      setLoading(true);
      setError(null);

      const placeId = parseInt(id);
      const [placeResponse, connectionsResponse] = await Promise.all([
        memoryService.getPlaceById(placeId),
        memoryService.getPlaceConnections(placeId)
      ]);

      if (placeResponse.success && placeResponse.place) {
        setPlace(placeResponse.place);
      } else {
        setError(placeResponse.message || 'Place not found');
      }

      if (connectionsResponse.success) {
        setOutgoingConnections(connectionsResponse.outgoing || []);
        setIncomingConnections(connectionsResponse.incoming || []);
        
        // Load connected places for graph view (T+1 only)
        const connectedPlaceIds = new Set<number>();
        (connectionsResponse.outgoing || []).forEach(conn => {
          connectedPlaceIds.add(conn.to_place_id);
        });
        (connectionsResponse.incoming || []).forEach(conn => {
          connectedPlaceIds.add(conn.from_place_id);
        });
        
        // Fetch all connected places
        if (connectedPlaceIds.size > 0) {
          setPlacesLoading(true);
          try {
            const placePromises = Array.from(connectedPlaceIds).map(placeId =>
              memoryService.getPlaceById(placeId)
            );
            const placeResponses = await Promise.all(placePromises);
            const places = placeResponses
              .filter(res => res.success && res.place)
              .map(res => res.place!);
            setConnectedPlaces(places);
          } catch (err) {
            console.error('Error loading connected places:', err);
          } finally {
            setPlacesLoading(false);
          }
        } else {
          setConnectedPlaces([]);
        }
      }
    } catch (err) {
      console.error('Error loading place data:', err);
      setError('Failed to load place data');
    } finally {
      setLoading(false);
    }
  }, [user?.id, id]);

  // Objects management functions - defined early so they can be used by other callbacks
  const getPlaceObjects = useCallback((): PlaceObject[] => {
    if (!place?.properties || typeof place.properties !== 'object') {
      return [];
    }
    const objects = place.properties.objects;
    return Array.isArray(objects) ? objects : [];
  }, [place?.properties]);

  const saveObjectsToPlace = useCallback(async (objectsToSave: PlaceObject[]) => {
    if (!place?.id) return;

    // Clear any pending save
    if (saveObjectsTimeoutRef.current) {
      clearTimeout(saveObjectsTimeoutRef.current);
    }

    // Debounce save operation
    saveObjectsTimeoutRef.current = setTimeout(async () => {
      try {
        setError(null);
        const currentProperties = place.properties || {};
        const updatedProperties = {
          ...currentProperties,
          objects: objectsToSave
        };

        const response = await memoryService.updatePlace(place.id, {
          properties: updatedProperties
        });

        if (response.success) {
          // Update place state directly without triggering loading state
          setPlace(prevPlace => {
            if (!prevPlace) return prevPlace;
            return {
              ...prevPlace,
              properties: updatedProperties
            };
          });
        } else {
          setError(response.message || 'Failed to save objects');
        }
      } catch (err) {
        console.error('Error saving objects:', err);
        setError('Failed to save objects');
      }
    }, 500); // 500ms debounce
  }, [place]);

  // Grid positioning utility - arranges objects in rows from top left
  const calculateGridPositions = useCallback((
    count: number,
    startX: number,
    startY: number,
    objectWidth: number,
    objectHeight: number,
    gap: number = 20,
    itemsPerRow: number = 5
  ): Array<{ x: number; y: number }> => {
    if (count === 0) return [];
    
    const positions: Array<{ x: number; y: number }> = [];
    
    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / itemsPerRow);
      const col = i % itemsPerRow;
      const x = startX + col * (objectWidth + gap);
      const y = startY + row * (objectHeight + gap);
      positions.push({ x: Math.round(x), y: Math.round(y) });
    }
    
    return positions;
  }, []);

  // Convert connected places to PlaceObjects and sync with existing objects
  // Only includes outgoing connections, excludes incoming connections
  const syncConnectedPlacesToObjects = useCallback(async () => {
    if (!place?.id) {
      return;
    }

    const existingObjects = getPlaceObjects();
    const existingOutgoingPlaceIds = new Set<number>();
    
    // Track which outgoing connected places already have objects
    // Filter out incoming connection objects
    existingObjects.forEach(obj => {
      if (obj.connectedPlaceId && obj.connectionDirection !== 'incoming') {
        existingOutgoingPlaceIds.add(obj.connectedPlaceId);
      }
    });

    // Create map to track outgoing connection place IDs
    const outgoingPlaceIds = new Set<number>();
    outgoingConnections.forEach(conn => outgoingPlaceIds.add(conn.to_place_id));

    // Filter existing objects to remove incoming connections and keep only outgoing and regular objects
    const filteredExistingObjects = existingObjects.filter(obj => {
      if (obj.connectedPlaceId) {
        // Only keep if it's an outgoing connection
        return obj.connectionDirection !== 'incoming' && outgoingPlaceIds.has(obj.connectedPlaceId);
      }
      // Keep all non-connected-place objects
      return true;
    });

    // Update existing objects with connection direction if missing
    const updatedExistingObjects = filteredExistingObjects.map(obj => {
      if (obj.connectedPlaceId) {
        // Ensure direction is set to outgoing
        return { ...obj, connectionDirection: 'outgoing' as const };
      }
      return obj;
    });

    // Filter connected places to only include outgoing connections
    const outgoingConnectedPlaces = connectedPlaces.filter(
      p => outgoingPlaceIds.has(p.id)
    );

    // Find outgoing connected places that don't have objects yet
    const newConnectedPlaces = outgoingConnectedPlaces.filter(
      p => !existingOutgoingPlaceIds.has(p.id)
    );

    if (newConnectedPlaces.length === 0) {
      // Update local objects state even if no new places (in case objects were loaded from place)
      if (activeTab === 'objects') {
        setObjects(updatedExistingObjects);
      }
      // Save filtered objects (removing incoming connections)
      if (updatedExistingObjects.length !== existingObjects.length) {
        await saveObjectsToPlace(updatedExistingObjects);
      }
      return;
    }

    console.log('Syncing connected places to objects:', newConnectedPlaces.length, 'new outgoing places');

    // Calculate positions for new connected places in rows from top left
    const startX = 50;
    const startY = 50;
    const objectWidth = 120;
    const objectHeight = 80;
    const gap = 20;
    const itemsPerRow = 5;
    
    // Calculate positions for all outgoing connected places (existing + new)
    const totalConnectedPlaces = existingOutgoingPlaceIds.size + newConnectedPlaces.length;
    const positions = calculateGridPositions(
      totalConnectedPlaces,
      startX,
      startY,
      objectWidth,
      objectHeight,
      gap,
      itemsPerRow
    );
    
    // Get positions for new places (skip positions for existing ones)
    const newPositions = positions.slice(existingOutgoingPlaceIds.size);

    // Convert new connected places to PlaceObjects (all are outgoing)
    const newPlaceObjects: PlaceObject[] = newConnectedPlaces.map((connectedPlace, index) => {
      return {
        id: `place-${connectedPlace.id}`,
        type: 'connected-place',
        name: connectedPlace.name,
        description: connectedPlace.description,
        x: newPositions[index]?.x ?? startX,
        y: newPositions[index]?.y ?? startY,
        width: objectWidth,
        height: objectHeight,
        connectedPlaceId: connectedPlace.id,
        connectionDirection: 'outgoing' as const
      };
    });

    // Merge with existing objects
    const updatedObjects = [...updatedExistingObjects, ...newPlaceObjects];
    
    console.log('Updated objects:', updatedObjects.length, 'total objects (outgoing only)');
    
    // Update local objects state immediately if on objects tab
    if (activeTab === 'objects') {
      setObjects(updatedObjects);
    }
    
    // Save to place properties (always save, even if not on objects tab)
    await saveObjectsToPlace(updatedObjects);
  }, [place?.id, connectedPlaces, outgoingConnections, getPlaceObjects, calculateGridPositions, saveObjectsToPlace, activeTab]);

  // Sync connected places to objects when connections change
  useEffect(() => {
    if (place) {
      if (connectedPlaces.length > 0) {
        console.log('Triggering sync for', connectedPlaces.length, 'connected places');
        syncConnectedPlacesToObjects();
      } else {
        // Also sync when connectedPlaces is empty (to load existing objects)
        syncConnectedPlacesToObjects();
      }
    }
  }, [place?.id, connectedPlaces, syncConnectedPlacesToObjects]);

  // Update objects state when place properties change (after save completes)
  // Filter out incoming connections
  useEffect(() => {
    if (place && activeTab === 'objects') {
      const loadedObjects = getPlaceObjects();
      // Filter out incoming connection objects
      const outgoingPlaceIds = new Set<number>();
      outgoingConnections.forEach(conn => outgoingPlaceIds.add(conn.to_place_id));
      
      const filteredObjects = loadedObjects.filter(obj => {
        if (obj.connectedPlaceId) {
          // Remove incoming connections, keep only outgoing
          return obj.connectionDirection !== 'incoming' && outgoingPlaceIds.has(obj.connectedPlaceId);
        }
        return true; // Keep non-connected-place objects
      });
      
      setObjects(filteredObjects);
    }
  }, [place?.properties?.objects, activeTab, getPlaceObjects, outgoingConnections]);

  // Reset note selection state when place changes
  useEffect(() => {
    setShowNoteSelection(false);
    setShowLinkExistingNote(false);
    setNoteSearchQuery('');
  }, [place?.id]);

  // Remove objects for deleted connections and incoming connections
  const cleanupDeletedConnections = useCallback(async () => {
    if (!place?.id) return;

    const existingObjects = getPlaceObjects();
    const outgoingPlaceIds = new Set<number>();
    
    // Collect only outgoing connected place IDs
    outgoingConnections.forEach(conn => outgoingPlaceIds.add(conn.to_place_id));

    // Remove objects for places that are no longer connected (outgoing) or are incoming connections
    const filteredObjects = existingObjects.filter(obj => {
      if (obj.connectedPlaceId) {
        // Remove if it's an incoming connection or not in outgoing connections
        if (obj.connectionDirection === 'incoming') {
          return false;
        }
        return outgoingPlaceIds.has(obj.connectedPlaceId);
      }
      return true; // Keep non-connected-place objects
    });

    // Only save if objects were removed
    if (filteredObjects.length !== existingObjects.length) {
      await saveObjectsToPlace(filteredObjects);
    }
  }, [place?.id, outgoingConnections, getPlaceObjects, saveObjectsToPlace]);

  // Cleanup deleted connections when connections change
  useEffect(() => {
    if (place) {
      cleanupDeletedConnections();
    }
  }, [place?.id, outgoingConnections.length, incomingConnections.length, cleanupDeletedConnections]);

  const handleUpdatePlace = async (placeData: { name: string; description?: string; properties?: Record<string, any> | string; is_root?: boolean }) => {
    if (!place?.id) return;

    try {
      setError(null);
      const response = await memoryService.updatePlace(place.id, placeData);

      if (response.success) {
        await loadPlaceData();
        setEditing(false);
      } else {
        setError(response.message || 'Failed to update place');
      }
    } catch (err) {
      console.error('Error updating place:', err);
      setError('Failed to update place');
    }
  };

  const handleStartEditingDescription = () => {
    setDescriptionValue(place?.description || '');
    setEditingDescription(true);
  };

  const handleSaveDescription = async () => {
    if (!place?.id) return;

    try {
      setError(null);
      const trimmedDescription = descriptionValue.trim() || undefined;
      const response = await memoryService.updatePlace(place.id, {
        description: trimmedDescription
      });

      if (response.success) {
        await loadPlaceData();
        setEditingDescription(false);
      } else {
        setError(response.message || 'Failed to update description');
      }
    } catch (err) {
      console.error('Error updating description:', err);
      setError('Failed to update description');
    }
  };

  const handleCancelEditingDescription = () => {
    setDescriptionValue(place?.description || '');
    setEditingDescription(false);
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      handleCancelEditingDescription();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSaveDescription();
    }
  };

  const handleDeletePlace = async () => {
    if (!place?.id) return;
    if (!window.confirm('Are you sure you want to delete this place? All connections will be removed.')) {
      return;
    }

    try {
      setError(null);
      const response = await memoryService.deletePlace(place.id);

      if (response.success) {
        navigate('/memory');
      } else {
        setError(response.message || 'Failed to delete place');
      }
    } catch (err) {
      console.error('Error deleting place:', err);
      setError('Failed to delete place');
    }
  };

  const handleDeleteConnection = async (connectionId: number) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) {
      return;
    }

    try {
      setError(null);
      const response = await memoryService.deleteConnection(connectionId);

      if (response.success) {
        await loadPlaceData();
      } else {
        setError(response.message || 'Failed to delete connection');
      }
    } catch (err) {
      console.error('Error deleting connection:', err);
      setError('Failed to delete connection');
    }
  };

  const handleConnectionCreated = () => {
    setShowConnectionForm(false);
    loadPlaceData();
  };

  const handlePlaceClick = (placeId: number) => {
    navigate(`/memory/${placeId}`);
  };

  const handleImageGenerated = async (imageUrl: string) => {
    if (!place?.id) return;

    try {
      setError(null);
      // Get current properties or create new object
      const currentProperties = place.properties || {};
      
      // Ensure images array exists
      const images = Array.isArray(currentProperties.images) 
        ? [...currentProperties.images, imageUrl]
        : [imageUrl];

      // Update properties with new image URL
      const updatedProperties = {
        ...currentProperties,
        images
      };

      const response = await memoryService.updatePlace(place.id, {
        properties: updatedProperties
      });

      if (response.success) {
        await loadPlaceData();
      } else {
        setError(response.message || 'Failed to save image');
      }
    } catch (err) {
      console.error('Error saving image:', err);
      setError('Failed to save image');
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!place?.id) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Optional: Validate file size (e.g., 10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('Image file size must be less than 10MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      // Upload to Firebase Storage
      const imageUrl = await uploadPlaceImage(place.id, file);

      // Get current properties or create new object
      const currentProperties = place.properties || {};
      
      // Ensure images array exists
      const images = Array.isArray(currentProperties.images) 
        ? [...currentProperties.images, imageUrl]
        : [imageUrl];

      // Update properties with new image URL
      const updatedProperties = {
        ...currentProperties,
        images
      };

      const response = await memoryService.updatePlace(place.id, {
        properties: updatedProperties
      });

      if (response.success) {
        await loadPlaceData();
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setError(response.message || 'Failed to save image');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleUploadButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteImage = async (imageUrl: string) => {
    if (!place?.id) return;
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setError(null);
      const currentProperties = place.properties || {};
      
      if (!Array.isArray(currentProperties.images)) {
        return;
      }

      // Remove the image URL from the array
      const images = currentProperties.images.filter((url: string) => url !== imageUrl);
      
      const updatedProperties = {
        ...currentProperties,
        images: images.length > 0 ? images : undefined
      };

      // Remove images key if array is empty
      if (images.length === 0) {
        delete updatedProperties.images;
      }

      const response = await memoryService.updatePlace(place.id, {
        properties: updatedProperties
      });

      if (response.success) {
        await loadPlaceData();
      } else {
        setError(response.message || 'Failed to delete image');
      }
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Failed to delete image');
    }
  };

  const getPlaceImages = (): string[] => {
    if (!place?.properties || typeof place.properties !== 'object') {
      return [];
    }
    const images = place.properties.images;
    return Array.isArray(images) ? images : [];
  };

  const getLinkedUrl = (): string | null => {
    if (!place?.properties || typeof place.properties !== 'object') {
      return null;
    }
    return place.properties.linkedUrl || null;
  };

  const getLinkedNoteId = useCallback((): string | null => {
    if (!place?.properties || typeof place.properties !== 'object') {
      return null;
    }
    return place.properties.linkedNoteId || null;
  }, [place?.properties]);

  // Load objects when place changes or Objects tab is activated
  // Filter out incoming connections
  useEffect(() => {
    if (place && activeTab === 'objects') {
      const loadedObjects = getPlaceObjects();
      // Filter out incoming connection objects
      const outgoingPlaceIds = new Set<number>();
      outgoingConnections.forEach(conn => outgoingPlaceIds.add(conn.to_place_id));
      
      const filteredObjects = loadedObjects.filter(obj => {
        if (obj.connectedPlaceId) {
          // Remove incoming connections, keep only outgoing
          return obj.connectionDirection !== 'incoming' && outgoingPlaceIds.has(obj.connectedPlaceId);
        }
        return true; // Keep non-connected-place objects
      });
      
      setObjects(filteredObjects);
      // Trigger sync if we have connected places
      if (connectedPlaces.length > 0) {
        syncConnectedPlacesToObjects();
      }
    }
  }, [place?.id, activeTab, getPlaceObjects, connectedPlaces.length, syncConnectedPlacesToObjects, outgoingConnections]);

  const handleCreateObject = useCallback((object: PlaceObject) => {
    const newObjects = [...objects, object];
    setObjects(newObjects);
    setShowObjectForm(false);
    setEditingObject(null);
    saveObjectsToPlace(newObjects);
  }, [objects, saveObjectsToPlace]);

  const handleUpdateObject = useCallback((updatedObject: PlaceObject) => {
    const newObjects = objects.map(obj => 
      obj.id === updatedObject.id ? updatedObject : obj
    );
    setObjects(newObjects);
    setShowObjectForm(false);
    setEditingObject(null);
    saveObjectsToPlace(newObjects);
  }, [objects, saveObjectsToPlace]);

  const handleDeleteObject = useCallback((objectId: string) => {
    const newObjects = objects.filter(obj => obj.id !== objectId);
    setObjects(newObjects);
    saveObjectsToPlace(newObjects);
  }, [objects, saveObjectsToPlace]);

  const handleMoveObject = useCallback((objectId: string, x: number, y: number) => {
    const newObjects = objects.map(obj => 
      obj.id === objectId ? { ...obj, x, y } : obj
    );
    setObjects(newObjects);
    saveObjectsToPlace(newObjects);
  }, [objects, saveObjectsToPlace]);

  const handleObjectClick = useCallback((object: PlaceObject) => {
    // Object selection is handled by ObjectsCanvas
  }, []);

  const handleObjectDoubleClick = useCallback((object: PlaceObject) => {
    // Don't allow editing connected place objects
    if (object.connectedPlaceId) {
      return;
    }
    setInitialObjectPosition(undefined);
    setEditingObject(object);
    setShowObjectForm(true);
  }, []);

  const handleNavigateToPlace = useCallback((placeId: number) => {
    navigate(`/memory/${placeId}`);
  }, [navigate]);

  const handleStartCreatingObject = useCallback(() => {
    // Get visible center position from canvas
    const visibleCenter = objectsCanvasRef.current?.getVisibleCenter();
    setInitialObjectPosition(visibleCenter || undefined);
    setEditingObject(null);
    setShowObjectForm(true);
  }, []);

  const handleObjectFormSave = useCallback((object: PlaceObject) => {
    if (editingObject) {
      handleUpdateObject(object);
    } else {
      handleCreateObject(object);
    }
    setInitialObjectPosition(undefined);
  }, [editingObject, handleUpdateObject, handleCreateObject]);

  const handleObjectFormCancel = useCallback(() => {
    setShowObjectForm(false);
    setEditingObject(null);
    setInitialObjectPosition(undefined);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveObjectsTimeoutRef.current) {
        clearTimeout(saveObjectsTimeoutRef.current);
      }
    };
  }, []);

  // Block manager for Notes tab
  const {
    blockManager,
    contentBlocks,
    loadBlocks,
    setContainerId
  } = useBlockManager({
    userId: user?.id || 0,
    containerType: 'note',
    containerId: linkedNote?.note_id,
    onContainerUpdate: async (container: BlockContainer) => {
      // Use ref instead of state to ensure we always have the latest linkedNote value
      const currentLinkedNote = linkedNoteRef.current;
      if (currentLinkedNote && user?.id) {
        try {
          console.log('📝 PlaceDetail: Updating note blocks for', currentLinkedNote.note_id, 'with blocks:', container.blocks);
          await NotesService.updateNote(currentLinkedNote.note_id, {
            title: currentLinkedNote.title,
            blocks: container.blocks.map((blockId, index) => ({
              block_id: blockId,
              order: index
            })),
            properties: currentLinkedNote.properties
          });
          console.log('✅ PlaceDetail: Note blocks updated successfully');
        } catch (err) {
          console.error('❌ PlaceDetail: Error updating note blocks:', err);
        }
      } else {
        console.warn('⚠️ PlaceDetail: Skipping note update - linkedNote or user not available', {
          hasLinkedNote: !!currentLinkedNote,
          hasUser: !!user?.id,
          containerId: container.id,
          blockCount: container.blocks.length
        });
      }
    }
  });

  const createLinkedNote = useCallback(async () => {
    if (!user?.id || !place?.id) return;

    try {
      setNoteLoading(true);
      setNoteError(null);

      // Use title as note_id (backend will sanitize it automatically)
      const title = `${place.name} - Notes`;

      // Create note
      const newNote = await NotesService.createNoteWithId(
        user.id,
        title,  // Use title as note_id
        title,  // Also use as title
        [],
        {
          linkedPlaceId: place.id
        }
      );

      // Update place properties with linked note_id
      const currentProperties = place.properties || {};
      const updatedProperties = {
        ...currentProperties,
        linkedNoteId: newNote.note_id
      };

      await memoryService.updatePlace(place.id, {
        properties: updatedProperties
      });

      // Reload place data to get updated properties
      await loadPlaceData();

      // Load the note
      const note = await NotesService.getNoteWithBlocks(newNote.note_id, user.id);
      setLinkedNote(note);
      setContainerId(note.note_id);
    } catch (err) {
      console.error('Error creating linked note:', err);
      setNoteError('Failed to create note');
    } finally {
      setNoteLoading(false);
    }
  }, [user?.id, place?.id, place?.name, place?.properties, loadPlaceData, setContainerId]);

  // Create linked note if it doesn't exist
  const ensureLinkedNote = useCallback(async () => {
    if (!user?.id || !place?.id) return;

    const existingNoteId = getLinkedNoteId();
    if (existingNoteId) {
      // Note already exists, try to load it
      try {
        setNoteLoading(true);
        setNoteError(null);
        const note = await NotesService.getNoteWithBlocks(existingNoteId, user.id);
        setLinkedNote(note);
        setContainerId(note.note_id);
        if (note.blocks && note.blocks.length > 0) {
          const blockIds = note.blocks.map(b => b.block_id);
          await loadBlocks(blockIds);
        }
      } catch (err) {
        console.error('Error loading linked note:', err);
        setNoteError('Failed to load note');
        // Note might have been deleted, create a new one
        await createLinkedNote();
      } finally {
        setNoteLoading(false);
      }
    } else {
      // Create new note
      await createLinkedNote();
    }
  }, [user?.id, place?.id, getLinkedNoteId, loadBlocks, setContainerId, createLinkedNote]);

  // Load available notes when showing note selection
  const loadAvailableNotes = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoadingAvailableNotes(true);
      const response = await NotesService.getUserNotes(user.id, {
        limit: 50,
        sort_order: 'DESC'
      });
      setAvailableNotes(response.data || []);
    } catch (err) {
      console.error('Error loading available notes:', err);
      setError('Failed to load notes');
    } finally {
      setLoadingAvailableNotes(false);
    }
  }, [user?.id]);

  // Link an existing note to the place
  const handleLinkExistingNote = useCallback(async (noteId: string) => {
    if (!place?.id || !user?.id) return;

    try {
      setNoteLoading(true);
      setNoteError(null);

      // Verify note exists and belongs to user
      const note = await NotesService.getNoteWithBlocks(noteId, user.id);

      // Update place properties with linked note_id
      const currentProperties = place.properties || {};
      const updatedProperties = {
        ...currentProperties,
        linkedNoteId: note.note_id
      };

      await memoryService.updatePlace(place.id, {
        properties: updatedProperties
      });

      // Reload place data to get updated properties
      await loadPlaceData();

      // Load the note
      setLinkedNote(note);
      setContainerId(note.note_id);
      setShowNoteSelection(false);
      setShowLinkExistingNote(false);
      setNoteSearchQuery('');
    } catch (err) {
      console.error('Error linking note:', err);
      setNoteError('Failed to link note');
    } finally {
      setNoteLoading(false);
    }
  }, [place?.id, user?.id, loadPlaceData, setContainerId]);

  // Load note when Notes tab is activated
  useEffect(() => {
    if (activeTab === 'notes' && place && user?.id) {
      const existingNoteId = getLinkedNoteId();
      if (existingNoteId) {
        // Note already linked, load it
        ensureLinkedNote();
        setShowNoteSelection(false);
        setShowLinkExistingNote(false);
      } else {
        // No note linked, show selection UI
        setShowNoteSelection(true);
        setShowLinkExistingNote(false);
      }
    }
  }, [activeTab, place?.id, user?.id, getLinkedNoteId, ensureLinkedNote]);

  // Filter notes based on search query
  const filteredNotes = availableNotes.filter(note =>
    note.title.toLowerCase().includes(noteSearchQuery.toLowerCase())
  );

  // Load notes when user chooses to link existing note
  const handleShowLinkExistingNote = useCallback(async () => {
    setShowLinkExistingNote(true);
    if (availableNotes.length === 0) {
      await loadAvailableNotes();
    }
  }, [availableNotes.length, loadAvailableNotes]);

  const handleGoToLink = () => {
    const linkedUrl = getLinkedUrl();
    if (!linkedUrl) return;

    const urlType = getUrlType(linkedUrl);
    if (urlType.type === 'internal') {
      navigate(urlType.path);
    } else {
      window.open(urlType.path, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCreatePlace = async (placeData: { name: string; description?: string; properties?: Record<string, any> | string; is_root?: boolean }) => {
    if (!user?.id) return;

    try {
      setError(null);
      const response = await memoryService.createPlace({
        user_id: user.id,
        ...placeData
      });

      if (response.success && response.id) {
        // Create connection if auto-connect is enabled
        if (autoConnect && place?.id) {
          try {
            await memoryService.createConnection({
              user_id: user.id,
              from_place_id: place.id,
              to_place_id: response.id
            });
          } catch (connErr) {
            console.error('Error creating connection:', connErr);
            // Don't fail the whole operation if connection creation fails
          }
        }
        
        setShowCreatePlaceForm(false);
        setAutoConnect(false);
        navigate(`/memory/${response.id}`);
      } else {
        setError(response.message || 'Failed to create place');
      }
    } catch (err) {
      console.error('Error creating place:', err);
      setError('Failed to create place');
    }
  };

  if (loading) {
    return (
      <div className="memory-palace-container">
        <div className="memory-palace-loading">Loading...</div>
      </div>
    );
  }

  if (error && !place) {
    return (
      <div className="memory-palace-container">
        <div className="memory-palace-error">{error}</div>
        <button onClick={() => navigate('/memory')} className="memory-palace-back-btn">
          Back to Memory Palace
        </button>
      </div>
    );
  }

  if (!place) {
    return null;
  }

  return (
    <div className="memory-palace-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/{place.name}</h1>}
        centerClassName="memory-palace-title"
        leftActions={
          <button onClick={() => navigate('/memory')} className="memory-palace-back-btn">
            ← Back
          </button>
        }
        rightActions={
          <div className="memory-palace-menu-container" ref={menuRef}>
            <button
              className="memory-palace-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
              title="Menu"
            >
              ⋮
            </button>
            {showMenu && (
              <div className="memory-palace-menu-dropdown">
                <button
                  className="memory-palace-menu-item"
                  onClick={() => {
                    setEditing(!editing);
                    setShowMenu(false);
                  }}
                >
                  {editing ? 'Cancel Edit' : 'Edit'}
                </button>
                <button
                  className="memory-palace-menu-item"
                  onClick={() => {
                    setShowImageGenerator(true);
                    setShowMenu(false);
                  }}
                >
                  Generate Image
                </button>
                <button
                  className="memory-palace-menu-item"
                  onClick={() => {
                    setShowCreatePlaceForm(true);
                    setShowMenu(false);
                  }}
                >
                  Create New Place
                </button>
                <button
                  className="memory-palace-menu-item"
                  onClick={() => {
                    setIsFullBlockMode(!isFullBlockMode);
                    setShowMenu(false);
                  }}
                >
                  Full Block Mode: {isFullBlockMode ? 'ON' : 'OFF'}
                </button>
                <button
                  className="memory-palace-menu-item"
                  onClick={() => {
                    handleDeletePlace();
                    setShowMenu(false);
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        }
      />

      {error && (
        <div className="memory-palace-error">{error}</div>
      )}

      {editing ? (
        <PlaceForm
          place={place}
          onSave={handleUpdatePlace}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <div className="memory-palace-detail">
            {/* Tab Content */}
            <div className="memory-palace-tab-content">
              {activeTab === 'description' && (
                <div className="memory-palace-tab-panel">
                  <div className="memory-palace-detail-section">
                    <div className="memory-palace-detail-section-header">
                      <h2 className="memory-palace-detail-section-title">Images</h2>
                      <button
                        className="memory-palace-action-btn"
                        onClick={handleUploadButtonClick}
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? 'Uploading...' : '+ Upload Photo'}
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileInputChange}
                      style={{ display: 'none' }}
                    />
                    {getPlaceImages().length > 0 && (
                      <div className="memory-palace-images-grid">
                        {getPlaceImages().map((imageUrl, index) => (
                          <div key={index} className="memory-palace-image-item">
                            <img
                              src={imageUrl}
                              alt={`${place.name} - Image ${index + 1}`}
                              className="memory-palace-image-thumbnail"
                              onClick={() => setViewingImage(imageUrl)}
                            />
                            <button
                              className="memory-palace-image-delete"
                              onClick={() => handleDeleteImage(imageUrl)}
                              title="Delete image"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {getLinkedUrl() && (
                    <div className="memory-palace-detail-section">
                      <button
                        className="memory-palace-link-btn"
                        onClick={handleGoToLink}
                      >
                        {getUrlType(getLinkedUrl()!).type === 'internal' ? '→' : '↗'} Go to Link
                      </button>
                    </div>
                  )}

                  <div className="memory-palace-detail-section">
                    <h2 className="memory-palace-detail-section-title">Description</h2>
                    {editingDescription ? (
                      <div className="memory-palace-description-editor">
                        <textarea
                          ref={descriptionTextareaRef}
                          value={descriptionValue}
                          onChange={(e) => setDescriptionValue(e.target.value)}
                          onKeyDown={handleDescriptionKeyDown}
                          className="memory-palace-description-textarea"
                          placeholder="Enter description..."
                          rows={6}
                        />
                        <div className="memory-palace-description-editor-actions">
                          <button
                            type="button"
                            onClick={handleSaveDescription}
                            className="memory-palace-description-save-btn"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEditingDescription}
                            className="memory-palace-description-cancel-btn"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="memory-palace-detail-content memory-palace-description-editable"
                        onClick={handleStartEditingDescription}
                        onFocus={handleStartEditingDescription}
                        tabIndex={0}
                        role="textbox"
                        aria-label="Description - click to edit"
                      >
                        {place.description || <span className="memory-palace-empty-text">No description - click to add</span>}
                      </div>
                    )}
                  </div>

                  <div className="memory-palace-detail-section">
                    <div className="memory-palace-detail-section-header">
                      <h2 className="memory-palace-detail-section-title">Connections</h2>
                      <button
                        className="memory-palace-action-btn"
                        onClick={() => setShowConnectionForm(true)}
                      >
                        + Add Connection
                      </button>
                    </div>

                    {showConnectionForm && user?.id && (
                      <ConnectionForm
                        userId={user.id}
                        fromPlaceId={place.id}
                        existingPlaces={[
                          place.id,
                          ...outgoingConnections.map(c => c.to_place_id),
                          ...incomingConnections.map(c => c.from_place_id)
                        ]}
                        places={allPlaces}
                        onSave={handleConnectionCreated}
                        onCancel={() => setShowConnectionForm(false)}
                      />
                    )}

                    {outgoingConnections.length === 0 && incomingConnections.length === 0 ? (
                      <div className="memory-palace-empty-text">No connections yet</div>
                    ) : (
                      <>
                        {outgoingConnections.length > 0 && (
                          <div className="memory-palace-connections-group">
                            <h3 className="memory-palace-connections-group-title">Connects To</h3>
                            <div className="memory-palace-connections-list">
                              {outgoingConnections.map((conn) => (
                                <div key={conn.id} className="memory-palace-connection-item">
                                  <button
                                    className="memory-palace-connection-link"
                                    onClick={() => handlePlaceClick(conn.to_place_id)}
                                  >
                                    → {conn.connected_place_name || `Place ${conn.to_place_id}`}
                                  </button>
                                  {conn.description && (
                                    <span className="memory-palace-connection-description">
                                      {conn.description}
                                    </span>
                                  )}
                                  <button
                                    className="memory-palace-connection-delete"
                                    onClick={() => handleDeleteConnection(conn.id)}
                                    title="Delete connection"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {incomingConnections.length > 0 && (
                          <div className="memory-palace-connections-group">
                            <h3 className="memory-palace-connections-group-title">Connected From</h3>
                            <div className="memory-palace-connections-list">
                              {incomingConnections.map((conn) => (
                                <div key={conn.id} className="memory-palace-connection-item">
                                  <button
                                    className="memory-palace-connection-link"
                                    onClick={() => handlePlaceClick(conn.from_place_id)}
                                  >
                                    ← {conn.connected_place_name || `Place ${conn.from_place_id}`}
                                  </button>
                                  {conn.description && (
                                    <span className="memory-palace-connection-description">
                                      {conn.description}
                                    </span>
                                  )}
                                  <button
                                    className="memory-palace-connection-delete"
                                    onClick={() => handleDeleteConnection(conn.id)}
                                    title="Delete connection"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="memory-palace-tab-panel">
                  {noteLoading ? (
                    <div className="memory-palace-loading">Loading note...</div>
                  ) : noteError ? (
                    <div className="memory-palace-error">{noteError}</div>
                  ) : showNoteSelection ? (
                    <div className="memory-palace-note-selection">
                      <div className="memory-palace-detail-section">
                        <h2 className="memory-palace-detail-section-title">Link a Note</h2>
                        <p className="memory-palace-note-selection-description">
                          Select an existing note to link to this place, or create a new system note.
                        </p>
                        
                        {showLinkExistingNote ? (
                          <div className="memory-palace-note-link-form">
                            <div className="memory-palace-note-search-container">
                              <input
                                type="text"
                                className="memory-palace-note-search-input"
                                placeholder="Search notes..."
                                value={noteSearchQuery}
                                onChange={(e) => setNoteSearchQuery(e.target.value)}
                                autoFocus
                              />
                            </div>
                            
                            {loadingAvailableNotes ? (
                              <div className="memory-palace-loading">Loading notes...</div>
                            ) : (
                              <>
                                {filteredNotes.length > 0 ? (
                                  <div className="memory-palace-note-selection-list">
                                    <div className="memory-palace-note-list">
                                      {filteredNotes.map((note) => (
                                        <button
                                          key={note.note_id}
                                          className="memory-palace-note-item"
                                          onClick={() => {
                                            handleLinkExistingNote(note.note_id);
                                            setNoteSearchQuery('');
                                          }}
                                        >
                                          <span className="memory-palace-note-item-title">{note.title}</span>
                                          {note.properties?.linkedPlaceId && (
                                            <span className="memory-palace-note-item-badge">Linked</span>
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ) : noteSearchQuery ? (
                                  <div className="memory-palace-empty-text">No notes found matching "{noteSearchQuery}"</div>
                                ) : (
                                  <div className="memory-palace-empty-text">No notes available</div>
                                )}
                                
                                <div className="memory-palace-note-selection-actions">
                                  <button
                                    className="memory-palace-action-btn"
                                    onClick={() => {
                                      setShowLinkExistingNote(false);
                                      setNoteSearchQuery('');
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="memory-palace-note-selection-actions">
                            <button
                              className="memory-palace-action-btn"
                              onClick={handleShowLinkExistingNote}
                            >
                              Link Existing Note
                            </button>
                            <button
                              className="memory-palace-action-btn"
                              onClick={async () => {
                                await createLinkedNote();
                                setShowNoteSelection(false);
                              }}
                            >
                              Create System Note
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : linkedNote ? (
                    <div className="memory-palace-notes-editor">
                      <BlockEditor
                        blockManager={blockManager}
                        blocks={contentBlocks}
                        placeholder="Start typing your notes..."
                        fullBlockMode={isFullBlockMode}
                      />
                    </div>
                  ) : (
                    <div className="memory-palace-empty-text">No note available</div>
                  )}
                </div>
              )}

              {activeTab === 'graph' && (
                <div className="memory-palace-tab-panel memory-palace-graph-tab-panel">
                  {placesLoading ? (
                    <div className="memory-palace-loading">Loading graph...</div>
                  ) : (
                    <MemoryPalaceGraph
                      places={place ? [place, ...connectedPlaces] : []}
                      connections={[...outgoingConnections, ...incomingConnections]}
                      onPlaceClick={handlePlaceClick}
                    />
                  )}
                </div>
              )}

              {activeTab === 'objects' && (
                <div className="memory-palace-tab-panel memory-palace-objects-tab-panel">
                  <div className="memory-palace-detail-section">
                    <div className="memory-palace-detail-section-header">
                      <h2 className="memory-palace-detail-section-title">Objects</h2>
                      <button
                        className="memory-palace-action-btn"
                        onClick={handleStartCreatingObject}
                      >
                        + Create Object
                      </button>
                    </div>
                    <ObjectsCanvas
                      ref={objectsCanvasRef}
                      objects={objects}
                      onObjectMove={handleMoveObject}
                      onObjectClick={handleObjectClick}
                      onObjectDelete={handleDeleteObject}
                      onObjectDoubleClick={handleObjectDoubleClick}
                      onNavigateToPlace={handleNavigateToPlace}
                      onObjectUpdate={handleUpdateObject}
                      placeId={place?.id}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Tab Navigation */}
            <div className="memory-palace-tabs">
              <button
                className={`memory-palace-tab ${activeTab === 'description' ? 'active' : ''}`}
                onClick={() => setActiveTab('description')}
              >
                Description
              </button>
              <button
                className={`memory-palace-tab ${activeTab === 'notes' ? 'active' : ''}`}
                onClick={() => setActiveTab('notes')}
              >
                Notes
              </button>
              <button
                className={`memory-palace-tab ${activeTab === 'graph' ? 'active' : ''}`}
                onClick={() => setActiveTab('graph')}
              >
                Graph
              </button>
              <button
                className={`memory-palace-tab ${activeTab === 'objects' ? 'active' : ''}`}
                onClick={() => setActiveTab('objects')}
              >
                Maps
              </button>
            </div>
          </div>
        </>
      )}

      {showImageGenerator && place && (
        <PlaceImageGenerator
          place={place}
          isOpen={showImageGenerator}
          onClose={() => setShowImageGenerator(false)}
          onImageGenerated={handleImageGenerated}
        />
      )}

      <ImageViewer
        imageUrl={viewingImage || ''}
        isOpen={!!viewingImage}
        onClose={() => setViewingImage(null)}
      />

      {showCreatePlaceForm && (
        <PlaceForm
          place={null}
          onSave={handleCreatePlace}
          onCancel={() => {
            setShowCreatePlaceForm(false);
            setAutoConnect(false);
          }}
          fromPlaceId={place?.id || null}
          autoConnect={autoConnect}
          onAutoConnectChange={setAutoConnect}
        />
      )}

      {showObjectForm && (
        <ObjectForm
          object={editingObject}
          onSave={handleObjectFormSave}
          onCancel={handleObjectFormCancel}
          initialPosition={editingObject ? undefined : initialObjectPosition}
        />
      )}
    </div>
  );
};

export default PlaceDetail;

